// React Query hooks for the super-admin system-logs surface.
//
// Endpoints:
//   GET    /super/logs                                   useSystemLogs
//   DELETE /super/logs                                   useDeleteLogs
//   GET    /super/logs/retention                         useLogRetention
//   PATCH  /super/logs/retention/:module/:severity       useUpdateRetention
//   GET    /super/platform-smtp                          usePlatformSmtp
//   PUT    /super/platform-smtp                          useUpsertPlatformSmtp
//
// `useSystemLogs` is the only polling query — refetchInterval 15s and a
// `refetchIntervalInBackground: false` flag pauses the poll when the tab
// is hidden (TanStack Query honours document.visibilityState natively).

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { useToast } from './useToast';

export type LogModule =
  | 'collections'
  | 'skip'
  | 'weighbridge'
  | 'mobile'
  | 'background'
  | 'platform'
  | 'auth';

export type LogSeverity = 'error' | 'warning' | 'info';

export type LogSource =
  | 'server'
  | 'web_client'
  | 'dispatch_app'
  | 'skip_manager_app';

export type LogCategory =
  | 'booking'
  | 'receipt'
  | 'notification'
  | 'sync'
  | 'cron'
  | 'auth'
  | 'api_error'
  | 'client_error'
  | 'skip_operation'
  | 'wb_operation'
  | 'payment';

export interface LogEntry {
  id: string;
  company_id: string | null;
  company_name: string | null;
  module: LogModule;
  category: LogCategory;
  severity: LogSeverity;
  source: LogSource;
  message: string;
  context: unknown;
  created_at: string;
}

export interface NotificationFailureEntry {
  source_table: 'message_queue';
  message_queue_uuid: string;
  company_id: string;
  channel_type: 'email' | 'sms' | 'whatsapp' | 'push';
  recipient: string;
  message: string;
  error_message: string | null;
  attempts: number;
  max_attempts: number;
  created_at: string;
}

export interface SystemLogsResponse {
  logs: LogEntry[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
  notification_failures?: NotificationFailureEntry[];
}

export interface SystemLogsFilters {
  module?: LogModule;
  category?: LogCategory;
  severity?: LogSeverity;
  source?: LogSource;
  company_id?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  per_page?: number;
}

function buildLogsQuery(filters: SystemLogsFilters): string {
  const params = new URLSearchParams();
  if (filters.module !== undefined && filters.module.length > 0) {
    params.set('module', filters.module);
  }
  if (filters.category !== undefined && filters.category.length > 0) {
    params.set('category', filters.category);
  }
  if (filters.severity !== undefined && filters.severity.length > 0) {
    params.set('severity', filters.severity);
  }
  if (filters.source !== undefined && filters.source.length > 0) {
    params.set('source', filters.source);
  }
  if (filters.company_id !== undefined && filters.company_id.length > 0) {
    params.set('company_id', filters.company_id);
  }
  if (filters.search !== undefined && filters.search.length > 0) {
    params.set('search', filters.search);
  }
  if (filters.from !== undefined && filters.from.length > 0) {
    params.set('from', filters.from);
  }
  if (filters.to !== undefined && filters.to.length > 0) {
    params.set('to', filters.to);
  }
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.per_page !== undefined) {
    params.set('per_page', String(filters.per_page));
  }
  return params.toString();
}

export function useSystemLogs(filters: SystemLogsFilters) {
  return useQuery<SystemLogsResponse>({
    queryKey: ['super', 'system-logs', filters],
    queryFn: async () => {
      const qs = buildLogsQuery(filters);
      const url = qs.length > 0
        ? `${endpoints.systemLogs.list}?${qs}`
        : endpoints.systemLogs.list;
      const res = await apiClient.get<SystemLogsResponse>(url);
      return res.data;
    },
    // Poll every 15s; TanStack Query suppresses background polling when
    // the tab is not visible (refetchIntervalInBackground defaults false).
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

export interface DeleteLogsInput {
  confirm: true;
  module?: LogModule;
  severity?: LogSeverity;
  before_date?: string;
}

interface DeleteLogsResult {
  deleted: number;
}

export function useDeleteLogs() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: DeleteLogsInput) => {
      const res = await apiClient.delete<DeleteLogsResult>(
        endpoints.systemLogs.delete,
        { data: body },
      );
      return res.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['super', 'system-logs'] });
      toast.success(`Deleted ${data.deleted} log entr${data.deleted === 1 ? 'y' : 'ies'}.`);
    },
    onError: () => {
      toast.error('Failed to delete logs.');
    },
  });
}

export interface RetentionRow {
  module: LogModule;
  severity: LogSeverity;
  retain_days: number;
  updated_at: string;
}

export function useLogRetention(enabled: boolean = true) {
  return useQuery<RetentionRow[]>({
    queryKey: ['super', 'log-retention'],
    queryFn: async () => {
      const res = await apiClient.get<RetentionRow[]>(
        endpoints.systemLogs.retention,
      );
      return res.data;
    },
    enabled,
    staleTime: 60_000,
  });
}

interface UpdateRetentionArgs {
  module: LogModule;
  severity: LogSeverity;
  retain_days: number;
}

export function useUpdateRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ module, severity, retain_days }: UpdateRetentionArgs) => {
      const res = await apiClient.patch<RetentionRow, { retain_days: number }>(
        endpoints.systemLogs.updateRetention(module, severity),
        { retain_days },
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super', 'log-retention'] });
    },
  });
}

export interface PlatformSmtpResponse {
  configured: boolean;
  host: string | null;
  port: number | null;
  secure: boolean;
  username: string | null;
  password: string | null;
  from_address: string | null;
  from_name: string | null;
  updated_at: string | null;
}

export interface UpsertPlatformSmtpInput {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password?: string;
  from_address: string;
  from_name: string;
}

export function usePlatformSmtp(enabled: boolean = true) {
  return useQuery<PlatformSmtpResponse>({
    queryKey: ['super', 'platform-smtp'],
    queryFn: async () => {
      const res = await apiClient.get<PlatformSmtpResponse>(
        endpoints.systemLogs.platformSmtp,
      );
      return res.data;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useUpsertPlatformSmtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpsertPlatformSmtpInput) => {
      const res = await apiClient.put<PlatformSmtpResponse, UpsertPlatformSmtpInput>(
        endpoints.systemLogs.platformSmtp,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super', 'platform-smtp'] });
    },
  });
}

export const MODULE_OPTIONS: LogModule[] = [
  'collections',
  'skip',
  'weighbridge',
  'mobile',
  'background',
  'platform',
  'auth',
];

export const SEVERITY_OPTIONS: LogSeverity[] = ['error', 'warning', 'info'];

export const SOURCE_OPTIONS: LogSource[] = [
  'server',
  'web_client',
  'dispatch_app',
  'skip_manager_app',
];

export const CATEGORY_OPTIONS: LogCategory[] = [
  'booking',
  'receipt',
  'notification',
  'sync',
  'cron',
  'auth',
  'api_error',
  'client_error',
  'skip_operation',
  'wb_operation',
  'payment',
];
